/**
 * DEC5 target cells as the page, the chat and the CSV read them. Pure. The
 * status always comes from the server's verdict (targetVerdict); nothing here
 * re-derives met / near / over from a rounded number (m4).
 *
 * @module services/looker/lookerTargetCells
 */
// Relative, never '@/': api/_lib/lookerTargets imports this module, and vite.config.ts
// loads that server code without the alias (an '@/' import fails `pnpm build`).
import { TARGET_OBJECTIVES, type TargetObjective } from '../targets/targetRules';
import type { LookerField, LookerRow, LookerTile, TileTargetCell } from './lookerTypes';
import type { ResultMetric } from '../targets/resultMetric';

const CPR = /\.cost_per_result$/;

/** The Cost per Result field the target columns follow; null = none. */
export const cprFieldOf = (tile: LookerTile): LookerField | null => tile.fields.find((f) => CPR.test(f.name)) ?? null;

/**
 * The objective a table row stands for: its first dimension, read against the one
 * list of objectives. null = no dimension, no row, or not exactly one objective.
 * The one "this row is Awareness" rule (DEC3 F8): target cells, the server's
 * tileResultMetricOf and the page's badge all read it.
 */
export const objectiveOfRow = (tile: { readonly fields: readonly LookerField[] }, row: LookerRow | undefined): TargetObjective | null => {
  const dim = tile.fields.find((f) => f.kind === 'dimension');
  const value = dim && row ? String(row[dim.name]?.value ?? '').trim().toLowerCase() : '';
  // The same rule as the server's oneObjectiveOf, from the one list of objectives (M3).
  return TARGET_OBJECTIVES.find((o): o is TargetObjective => o === value) ?? null;
};

/** A table row's cell by its first dimension (an objective); a single value's only cell. undefined = not judged. */
export const targetCellOf = (tile: LookerTile, row: LookerRow | undefined): TileTargetCell | undefined => {
  const targets = tile.targets;
  if (!targets) return undefined;
  if (tile.type === 'single_value') return Object.values(targets.byObjective)[0];
  const objective = objectiveOfRow(tile, row);
  return objective ? targets.byObjective[objective] : undefined;
};

/** deltaPct is already a percent: +14.8% / −25.0% (a true minus sign). */
export const formatTargetDelta = (deltaPct: number): string =>
  `${deltaPct > 0 ? '+' : deltaPct < 0 ? '−' : ''}${Math.abs(deltaPct).toFixed(1)}%`;

/** The delta is shown only beside a verdict. */
export const hasVerdict = (c: TileTargetCell): c is TileTargetCell & { deltaPct: number } =>
  (c.status === 'met' || c.status === 'near' || c.status === 'over25') && c.deltaPct !== null;

export const TARGET_TONE: Record<TileTargetCell['status'], string> = {
  met: 'text-success-700', near: 'text-neutral-700', over25: 'text-error-700',
  none: 'text-neutral-700', na: 'text-neutral-700', failed: 'text-neutral-700', noCurrency: 'text-neutral-700', otherMetric: 'text-neutral-700',
};

/** Product terms, the same in both languages: "เป้าตั้งไว้กับ Impressions". */
export const RESULT_METRIC_NAME: Record<ResultMetric, string> = {
  impressions: 'Impressions', reach: 'Reach', clicks: 'Clicks', conversions: 'Conversions',
};
